import { useState } from 'react';
import { Plus, Pencil, Trash2, FolderOpen } from 'lucide-react';
import { deleteTable, insertTable, updateTable } from '@/lib/api';
import type { Category } from '@/types';
import { Modal } from '@/components/Modal';

interface CategoryManagerProps {
  categories: Category[];
  onRefresh: () => void;
}

export function CategoryManager({ categories, onRefresh }: CategoryManagerProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [name, setName] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [saving, setSaving] = useState(false);

  const openNew = () => {
    setEditing(null);
    setName('');
    setSortOrder(0);
    setModalOpen(true);
  };

  const openEdit = (c: Category) => {
    setEditing(c);
    setName(c.name);
    setSortOrder(c.sort_order);
    setModalOpen(true);
  };

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (editing) {
        await updateTable('categories', { id: editing.id, name, sort_order: sortOrder });
      } else {
        await insertTable('categories', { name, sort_order: sortOrder });
      }
      setModalOpen(false);
      onRefresh();
    } catch {
      alert('Erro ao salvar categoria.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c: Category) => {
    if (!confirm(`Excluir categoria "${c.name}"? Todos os produtos dentro dela também serão excluídos.`)) return;
    await deleteTable('categories', Number(c.id));
    onRefresh();
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display text-xl font-bold text-warm-900">Categorias</h2>
        <button onClick={openNew} className="btn-primary">
          <Plus size={18} /> Nova Categoria
        </button>
      </div>

      {categories.length === 0 ? (
        <div className="card flex flex-col items-center justify-center py-12 text-center">
          <FolderOpen size={40} className="text-warm-300" />
          <p className="mt-3 text-warm-500">Nenhuma categoria cadastrada.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {categories.map((c) => (
            <div key={c.id} className="card flex items-center gap-3 p-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-500">
                <FolderOpen size={18} />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-warm-900">{c.name}</h3>
                <p className="text-xs text-warm-500">Ordem: {c.sort_order}</p>
              </div>
              <button
                onClick={() => openEdit(c)}
                className="flex h-9 w-9 items-center justify-center rounded-lg bg-warm-100 text-warm-600 transition-colors hover:bg-warm-200"
              >
                <Pencil size={16} />
              </button>
              <button
                onClick={() => remove(c)}
                className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 text-red-500 transition-colors hover:bg-red-100"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar Categoria' : 'Nova Categoria'}
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-warm-700">Nome da categoria</label>
            <input
              className="input-field"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Pizzas, Bebidas, Sobremesas"
              autoFocus
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-warm-700">Ordem de exibição</label>
            <input
              type="number"
              className="input-field"
              value={sortOrder}
              onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
            />
          </div>
          <button onClick={save} disabled={saving} className="btn-primary w-full">
            {saving ? 'Salvando...' : 'Salvar Categoria'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
